'use client';
import React, { createContext, useContext, useEffect } from 'react';
import axios from 'axios';
import { parse, serialize } from 'cookie'; // <-- Replaces js-cookie
import { toast } from 'react-hot-toast';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';

// @tanstack/react-query imports:
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';

/**
 * Types & Interfaces
 */
interface CartItemAttributeValue {
  id: number;
  cartItemId: number;
  attributeId: string;
  attributeValueId: string;
  attributeValue?: any;
  value?: string;
}

export interface CartItemType {
  id: number;
  productId: string;
  variantId?: string;
  quantity: number;
  attributes?: CartItemAttributeValue[];
  personalization: string | null;
}

interface AddToCartParams {
  productId: string;
  variantId?: string;
  attributes?: { attributeId: string; attributeValueId: string }[];
  quantity?: number;
  personalization: string | null;
  productPrice: number; // might be used only server-side
}

interface EditCartItemParams {
  quantity?: number;
  variantId?: string;
  attributes?: { attributeId: string; attributeValueId: string }[];
  personalization: string | null;
}

interface CartContextType {
  cart: CartItemType[];
  loading: boolean;
  addToCart: (item: AddToCartParams) => Promise<void>;
  editCartItem: (cartItemId: number, updatedItem: Partial<EditCartItemParams>) => Promise<void>;
  removeFromCart: (cartItemId: number) => Promise<void>;
  getProductQuantity: (cartItemId: number) => number;
  clearCart: () => Promise<void>;
}

/**
 * Create & Export the Context + Hook
 */
const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

/**
 * Utility functions to manage cookies using `cookie` library
 */
function getCookieValue(key: string) {
  if (typeof document === 'undefined') return null;
  const cookies = parse(document.cookie || '');
  return cookies[key] || null;
}

function setCookieValue(key: string, value: string, days = 7) {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60; // convert days to seconds
  const cookie = serialize(key, value, {
    path: '/',
    maxAge,
  });
  document.cookie = cookie;
}

function removeCookieValue(key: string) {
  if (typeof document === 'undefined') return;
  const cookie = serialize(key, '', {
    path: '/',
    maxAge: 0,
  });
  document.cookie = cookie;
}

/**
 * CartProvider Component
 */
interface CartProviderProps {
  children: React.ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();

  /**
   * Fetch the cart items from the server if logged in, or from cookies if not.
   */
  const fetchCart = async (): Promise<CartItemType[]> => {
    if (user) {
      const { data } = await axios.get('/api/cart', { withCredentials: true });
      return data.cartItems || [];
    }
    // For guests, read from cookie
    const cartCookie = getCookieValue('cart');
    const parsed: CartItemType[] = cartCookie ? JSON.parse(cartCookie) : [];
    // Ensure `attributes` is always an array
    return parsed.map((item) => ({
      ...item,
      attributes: item.attributes || [],
    }));
  };

  /**
   * useQuery: Retrieve cart data
   */
  const {
    data: cart = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['cart'],
    queryFn: fetchCart,
    enabled: !authLoading,       // Only fetch once Auth is resolved
    staleTime: 5 * 60 * 1000,    // 5 minutes
    refetchOnWindowFocus: false, // We'll refetch only in specific scenarios
    refetchOnMount: false,       // We'll manually control initial fetch
  });

  /**
   * Force a refetch only when user navigates to "/cart" or "/checkout".
   */
  const pathname = usePathname();

  useEffect(() => {
    if (pathname === '/cart' || pathname === '/checkout') {
      refetch(); // Refetch cart data when navigating to cart or checkout
    }
  }, [pathname, refetch]);

  // -------------------------------------------------------
  // Mutations
  // -------------------------------------------------------

  /**
   * Add to Cart
   */
  const addToCartMutation = useMutation({
    mutationFn: (item: AddToCartParams) =>
      axios.post('/api/cart/add', item, { withCredentials: true }),
    onSuccess: (response) => {
      // If server returns updated cart, update the query data
      const { cartItems } = response.data;
      queryClient.setQueryData<CartItemType[]>(['cart'], cartItems || []);
      // Then force a refetch to ensure no mismatch
      refetch();

      removeCookieValue('cart'); // Clear guest cart cookie if user is logged in
      toast.success('Продуктът е добавен в количката!', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    },
    onError: (error: any) => {
      console.error('Error adding to cart:', error);
      toast.error(error.response?.data?.message || 'Неуспешно добавяне в количката.');
    },
  });

  /**
   * Edit Cart Item
   */
  const editCartItemMutation = useMutation({
    mutationFn: ({ cartItemId, updatedItem }: { cartItemId: number; updatedItem: Partial<EditCartItemParams> }) =>
      axios.put(`/api/cart/edit/${cartItemId}`, updatedItem, { withCredentials: true }),
    onSuccess: (response, variables) => {
      const { cartItems } = response.data || {};
      if (cartItems) {
        queryClient.setQueryData<CartItemType[]>(['cart'], cartItems);
      }
      // Ensure final sync
      refetch();

      toast.success('Продуктът е редактиран успешно в количката.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    },
    onError: (error: any) => {
      console.error('Error editing cart item:', error);
      toast.error(error.response?.data?.message || 'Неуспешно редактиране на продукта в количката.');
    },
  });

  /**
   * Remove from Cart
   */
  const removeFromCartMutation = useMutation({
    mutationFn: (cartItemId: number) =>
      axios.delete(`/api/cart/remove/${cartItemId}`, { withCredentials: true }),
    onSuccess: (response, cartItemId) => {
      const { cartItems } = response.data;
      if (cartItems) {
        queryClient.setQueryData<CartItemType[]>(['cart'], cartItems);
      } else {
        // Or else just remove from the existing cart in the cache
        queryClient.setQueryData<CartItemType[]>(['cart'], (oldCart = []) =>
          oldCart.filter((item) => item.id !== cartItemId)
        );
      }
      // Force final refetch
      refetch();

      toast.success('Продуктът е премахнат успешно от количката.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    },
    onError: (error: any) => {
      console.error('Error removing item from cart:', error);
      toast.error('Неуспешно премахване на продукта от количката.');
    },
  });

  /**
   * Clear Cart
   */
  const clearCartMutation = useMutation({
    mutationFn: () => axios.delete('/api/cart', { withCredentials: true }),
    onSuccess: (response) => {
      const { cartItems } = response.data || {};
      // If server sent updated cart data
      queryClient.setQueryData<CartItemType[]>(['cart'], cartItems || []);
      refetch();

      toast.success('Количката е изчистена успешно.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    },
    onError: () => {
      toast.error('Неуспешно изчистване на количката.');
    },
  });

  // -------------------------------------------------------
  // Public Methods
  // -------------------------------------------------------

  /**
   * Add to Cart
   * If there is a user, use the server. If not, store in cookies.
   */
  const addToCart = async (item: AddToCartParams) => {
    if (user) {
      await addToCartMutation.mutateAsync(item);
    } else {
      // Handle guest cart via Cookie
      const newItem: CartItemType = {
        id: Date.now(),
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity || 1,
        personalization: item.personalization || '',
        attributes:
          item.attributes?.map((attr) => ({
            id: Date.now() + Math.random(),
            cartItemId: Date.now(),
            attributeId: attr.attributeId,
            attributeValueId: attr.attributeValueId,
          })) || [],
      };

      const existing = JSON.parse(getCookieValue('cart') || '[]') as CartItemType[];
      const updated = [...existing, newItem];
      setCookieValue('cart', JSON.stringify(updated));

      toast.success('Продуктът е добавен успешно в количката.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    }
  };

  /**
   * Edit Cart
   * If there is a user, use the server. If not, store in cookies.
   */
  const editCartItem = async (cartItemId: number, updatedItem: Partial<EditCartItemParams>) => {
    if (user) {
      await editCartItemMutation.mutateAsync({ cartItemId, updatedItem });
    } else {
      // Handle guest cart via Cookie
      const existing = JSON.parse(getCookieValue('cart') || '[]') as CartItemType[];
      const updated = existing.map((item) =>
        item.id === cartItemId ? { ...item, ...updatedItem } : item
      );
      setCookieValue('cart', JSON.stringify(updated));

      toast.success('Продуктът е редактиран успешно в количката.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    }
  };

  /**
   * Remove from Cart
   * If there is a user, use the server. If not, store in cookies.
   */
  const removeFromCart = async (cartItemId: number) => {
    if (user) {
      await removeFromCartMutation.mutateAsync(cartItemId);
    } else {
      // Handle guest cart via Cookie
      const existing = JSON.parse(getCookieValue('cart') || '[]') as CartItemType[];
      const updated = existing.filter((item) => item.id !== cartItemId);
      setCookieValue('cart', JSON.stringify(updated));

      toast.success('Продуктът е премахнат успешно от количката.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    }
  };

  /**
   * Clear Cart
   * If there is a user, use the server. If not, store in cookies.
   */
  const clearCart = async () => {
    if (user) {
      await clearCartMutation.mutateAsync();
    } else {
      removeCookieValue('cart');
      toast.success('Количката е изчистена успешно.', {
        style: { border: '1px solid #713200', padding: '16px', color: '#713200' },
        iconTheme: { primary: '#713200', secondary: '#FFFAEE' },
      });
    }
  };

  /**
   * Get product quantity from the cart (by ID)
   */
  const getProductQuantity = (cartItemId: number): number => {
    // For guests, read from cookie; for logged in users, read from server data
    const current = user ? cart : JSON.parse(getCookieValue('cart') || '[]');
    const found = current.find((item: CartItemType) => item.id === cartItemId);
    return found ? found.quantity : 0;
  };

  // -------------------------------------------------------
  // Provide Context
  // -------------------------------------------------------
  return (
    <CartContext.Provider
      value={{
        cart: user ? cart : JSON.parse(getCookieValue('cart') || '[]'),
        loading: isLoading,
        addToCart,
        editCartItem,
        removeFromCart,
        getProductQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
